import { useEffect, useState } from 'react';

import {
  AgencyAvailabilityService,
  AgencyBookingRateService,
} from 'us-web-services';

import { AgencySitterCard } from './AgencySitterCard';

interface Props {
  city: string;
  state: string;
  street: string;
  zipCode: string;
  numberOfChildren: number;
  startDate: string;
  endDate: string;
}

// Same rate-resolution flow as AgencyWidget, but renders the narrower
// AgencySitterCard so it fits one cell of the SitterCard grid.
export const AgencySitterCardWidget = (props: Props): JSX.Element | null => {
  const [rate, setRate] = useState<number | null>(null);

  useEffect(() => {
    if (!props.zipCode) {
      setRate(null);
      return;
    }
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const availability = await AgencyAvailabilityService.getByFilter({
          params: { zipCode: props.zipCode },
        });
        if (cancelled) return;
        if (!availability.data?.data?.length) {
          setRate(null);
          return;
        }

        const bookingRates = await AgencyBookingRateService.getByFilter({
          params: { zip: props.zipCode },
        });
        if (cancelled) return;
        const record = bookingRates.data?.data?.[0] as
          | Record<string, number | undefined>
          | undefined;
        if (!record) {
          setRate(null);
          return;
        }

        let num = Math.max(1, Math.min(4, props.numberOfChildren || 1));
        let resolved = record[`careFor${num}`];
        while (!resolved && num > 0) {
          num -= 1;
          resolved = record[`careFor${num}`];
        }
        setRate(resolved ?? null);
      } catch (err) {
        console.warn('[AgencySitterCardWidget] failed to load rate', err);
        setRate(null);
      }
    };

    load();
    return (): void => {
      cancelled = true;
    };
  }, [props.zipCode, props.numberOfChildren]);

  if (!rate || rate <= 0) return null;

  return (
    <AgencySitterCard
      city={props.city}
      endDate={props.endDate}
      numberOfChildren={props.numberOfChildren}
      rate={rate}
      startDate={props.startDate}
      state={props.state}
      street={props.street}
      zipCode={props.zipCode}
    />
  );
};
